import {
  Linking,
  ScrollView,
  StyleSheet,
  Text,
  Touchable,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import React, {
  FC,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { STYLE_VARS } from '../../../constants/style-vars';
import { ICourt } from '../../../models/ICourt';
import BookingPopup from './BookingPopup';
import dayjs from 'dayjs';
import {
  convertToAmPm,
  increaseHourByMinutes,
} from '../../../services/utils/timeConvert';
import { useIsDesktop } from '../../../hooks/useIsDeskTop';
import { Image } from '../../../components/Image';
import { COLORS, FONTS } from '../../../constants';
import SportApi from '../../../services/api/sports.api';
import { Alert } from '../../../services/alert';
import { Loading } from '../../../components';
import { IFacilities } from '../../../types/sports.types';
import { IS_WEB } from '../../../constants/platform';

interface CourtBookingProps {
  data: ICourt;
  navDay: string;
  handleChangeDate: (date: string) => void;
  facility?: IFacilities;
}

const CourtBooking: FC<CourtBookingProps> = ({
  data,
  navDay,
  handleChangeDate,
  facility,
}) => {
  const today = useMemo(() => {
    return new Date(data.today);
  }, [data]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [popupContent, setPopupContent] = useState<ReactNode>();
  const [isBookingSlot, setIsBookingSlot] = useState<boolean>(false);
  const isDesktop = useIsDesktop();
  const { width } = useWindowDimensions();
  const scrollViewRef = useRef<ScrollView>(null);
  const [currentPosition, setCurrentPosition] = useState<number>(0);
  const timeWidth = isDesktop() ? (1250 * 0.75) / 7 : width / 3;
  const maxColShow: number = isDesktop() ? 6 : 2;
  const courtBookingWidth: number = isDesktop() ? 1250 * 0.75 : width;
  const [selectedSlot, setSelectedSlot] = useState<{
    time: string;
    courtNum: string;
    courtName: string;
  }>();

  const handleSubmitBooking = useCallback(() => {
    setIsBookingSlot(true);
    setIsModalVisible(false);
    const selectDate = dayjs(new Date(navDay)).format('YYYYMMDD');

    const submitData = {
      date: selectDate,
      time: selectedSlot?.time,
      courtNum: selectedSlot?.courtNum,
      bookSheet: data.selectedSheet,
    };

    let slotInfo = IS_WEB
      ? `${dayjs(new Date(navDay)).format('DD MMMM YYYY')}<br/>${
          selectedSlot?.time
        } - ${increaseHourByMinutes(
          selectedSlot?.time ?? '',
          parseInt(data.duration)
        )}<br/>${selectedSlot?.courtName}<br/>`
      : `${dayjs(new Date(navDay)).format('DD MMMM YYYY')}\n${
          selectedSlot?.time
        } - ${increaseHourByMinutes(
          selectedSlot?.time ?? '',
          parseInt(data.duration)
        )}\n${selectedSlot?.courtName}\n`;

    SportApi.postSport(submitData)
      .then((res) => {
        if (res.data.booking_id.length !== 0) {
          Alert.alert(
            'Success',
            slotInfo + `You successfully booked this time slot`
          );
        } else {
          Alert.alert('Error', res.data.return_code);
        }
        handleChangeDate(navDay);
      })
      .catch((error) => {
        Alert.alert('Error', error.return_code);
      })
      .finally(() => {
        setIsBookingSlot(false);
      });
  }, [selectedSlot, data, navDay, handleChangeDate]);

  const itemWidth = useMemo(() => {
    const courtListWidth: number = courtBookingWidth - timeWidth;
    const splitCol =
      data.courts.length < maxColShow ? data.courts.length : maxColShow;

    return courtListWidth / splitCol;
  }, [timeWidth, data.courts.length, maxColShow, courtBookingWidth]);

  const timeSequence = useMemo(() => {
    const result = [];
    const startTime = new Date(`2000-01-01T${data.bstart}:00`);
    const endTime = new Date(`2000-01-01T${data.bend}:00`);

    let currentTime = startTime;
    while (currentTime <= endTime) {
      const hours = currentTime.getHours().toString().padStart(2, '0');
      const minutes = currentTime.getMinutes().toString().padStart(2, '0');
      const formattedTime = `${hours}:${minutes}`;
      result.push(formattedTime);
      currentTime.setMinutes(
        currentTime.getMinutes() + parseInt(data.binterval)
      );
    }

    return result;
  }, [data]);

  const renderTimeColumn = useMemo(() => {
    const timeContentWidth = timeWidth;
    const timeItems: ReactNode[] = [];
    timeItems.push(
      <View
        style={{
          width: timeContentWidth,
          height: 50,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: STYLE_VARS.primaryColor,
        }}
        key={'Time'}
      >
        <Text style={[styles.timeTextStyle, { color: 'white' }]}>Time</Text>
      </View>
    );
    for (let i = 0; i < timeSequence.length; i++) {
      timeItems.push(
        <View
          style={{
            width: timeContentWidth,
            height: 50,
            alignItems: 'center',
            justifyContent: 'center',
          }}
          key={timeSequence[i]}
        >
          <Text style={styles.timeTextStyle}>
            {convertToAmPm(timeSequence[i])}
          </Text>
        </View>
      );
    }
    return timeItems;
  }, [timeSequence, timeWidth]);

  const renderItems = useMemo(() => {
    const Items: ReactNode[] = [];
    for (let i = 0; i < data.ecodes.length; i++) {
      const ColumnItem: ReactNode[] = [];
      ColumnItem.push(
        <View
          key={data.courts[i].code}
          style={[
            styles.itemWrapper,
            {
              backgroundColor: STYLE_VARS.primaryColor,
              width: itemWidth,
            },
          ]}
        >
          <Text
            style={[styles.timeTextStyle, { color: 'white' }]}
            numberOfLines={1}
          >
            {data.courts[i].name}
          </Text>
        </View>
      );
      const ecode = data.ecodes[i];
      for (let j = 0; j < timeSequence.length; j++) {
        const curTimeStr = timeSequence[j];
        let busySlot = false;
        for (let k = 0; k < data.booked_slots.length; k++) {
          const slot = data.booked_slots[k];
          if (
            slot.belement === ecode &&
            slot.btime <= curTimeStr &&
            slot.etime > curTimeStr
          ) {
            ColumnItem.push(
              <View
                key={j}
                style={[
                  styles.itemWrapper,
                  {
                    backgroundColor:
                      i % 2 === j % 2 ? STYLE_VARS.fccLightGrey : undefined,
                  },
                ]}
              >
                <Text style={styles.itemTextStyle}>
                  {slot.bstatus === 'B' ? 'Booked' : 'Full'}
                </Text>
              </View>
            );
            busySlot = true;
            break;
          }
        }
        if (!busySlot) {
          ColumnItem.push(
            <TouchableOpacity
              key={j}
              style={[
                styles.itemWrapper,
                {
                  backgroundColor:
                    i % 2 === j % 2 ? STYLE_VARS.fccLightGrey : undefined,
                },
              ]}
              onPress={() => {
                setPopupContent(
                  <View
                    style={{
                      paddingHorizontal: isDesktop() ? 0 : 10,
                      width: isDesktop() ? 220 : undefined,
                      alignSelf: 'center',
                      // alignSelf: 'flex-start',
                    }}
                  >
                    <Text
                      style={[
                        styles.itemTextStyle,
                        {
                          fontSize: isDesktop() ? 16 : undefined,
                          marginBottom: isDesktop() ? 20 : 10,
                          textAlign: 'center',
                        },
                      ]}
                    >
                      Make a booking?
                    </Text>
                    <View
                      style={{
                        flexDirection: 'column',
                        gap: 4,
                        marginBottom: 20,
                      }}
                    >
                      <Text style={styles.timeTextStyle}>
                        SPORT: {facility?.bs_desc}
                      </Text>
                      <Text style={styles.timeTextStyle}>
                        DATE:{' '}
                        {dayjs(new Date(navDay)).format('ddd DD MMM YYYY')}
                      </Text>
                      <Text style={styles.timeTextStyle}>
                        TIME: {convertToAmPm(curTimeStr)} -{' '}
                        {convertToAmPm(
                          increaseHourByMinutes(
                            curTimeStr,
                            parseInt(data.duration)
                          )
                        )}
                      </Text>
                      <Text style={styles.timeTextStyle}>
                        VENUE: {data.courts[i].name}
                      </Text>
                      <Text style={styles.timeTextStyle}>
                        {'By clicking confirm you agree to the bye-laws'}
                      </Text>
                      <TouchableOpacity
                        onPress={() => {
                          Linking.openURL(data.bye_laws.url);
                        }}
                      >
                        <Text
                          style={[
                            styles.timeTextStyle,
                            {
                              color: COLORS.GREEN,
                              textDecorationLine: 'underline',
                              textTransform: 'uppercase',
                            },
                          ]}
                        >
                          KCC Bye Laws
                        </Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                );
                setSelectedSlot({
                  time: curTimeStr,
                  courtNum: data.courts[i].code,
                  courtName: data.courts[i].name,
                });
                setIsModalVisible(true);
              }}
            >
              <Text style={styles.itemTextStyle}>Available</Text>
            </TouchableOpacity>
          );
        }
      }
      Items.push(
        <View style={{ flexDirection: 'column' }} key={i}>
          {ColumnItem}
        </View>
      );
    }
    return Items;
  }, [data, timeSequence, isDesktop, itemWidth, facility, navDay]);

  return (
    <>
      <View style={{ zIndex: 0 }}>
        <View
          style={{
            paddingVertical: 20,
            flexDirection: 'row',
          }}
        >
          <View style={{ flexDirection: 'column', zIndex: 1 }}>
            {renderTimeColumn}
          </View>
          <ScrollView
            ref={scrollViewRef}
            horizontal={true}
            style={{ zIndex: 1 }}
          >
            {renderItems}
          </ScrollView>
          {data.ecodes.length > maxColShow && (
            <>
              <TouchableOpacity
                style={{
                  top: 38,
                  right: 10,
                  zIndex: 100,
                  position: 'absolute',
                  width: 20,
                  alignItems: 'flex-end',
                }}
                onPress={() => {
                  if (scrollViewRef.current) {
                    const newPosition = currentPosition + itemWidth;
                    if (
                      newPosition <=
                      (data.ecodes.length - (isDesktop() ? 6 : 2)) * itemWidth
                    ) {
                      scrollViewRef.current.scrollTo({
                        x: newPosition,
                        animated: true,
                      });
                      setCurrentPosition(newPosition);
                    }
                  }
                }}
              >
                <Image
                  source={require('../../../assets/right-arrow.png')}
                  style={{
                    width: 8,
                    height: 13,
                    resizeMode: 'contain',
                    tintColor: 'white',
                  }}
                />
              </TouchableOpacity>
              <TouchableOpacity
                style={{
                  top: 38,
                  left: timeWidth,
                  zIndex: 100,
                  position: 'absolute',
                  width: 20,
                  alignItems: 'flex-start',
                }}
                onPress={() => {
                  if (scrollViewRef.current) {
                    const newPosition =
                      currentPosition - itemWidth < 0
                        ? 0
                        : currentPosition - itemWidth;
                    scrollViewRef.current.scrollTo({
                      x: newPosition,
                      animated: true,
                    });
                    setCurrentPosition(newPosition);
                  }
                }}
              >
                <Image
                  source={require('../../../assets/left-arrow.png')}
                  style={{
                    width: 8,
                    height: 13,
                    resizeMode: 'contain',
                    tintColor: 'white',
                  }}
                />
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>
      <BookingPopup
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
        handleConfirm={() => {
          handleSubmitBooking();
        }}
      >
        {popupContent}
      </BookingPopup>
      {isBookingSlot === true && <Loading isFullScreen={true} />}
    </>
  );
};

export default CourtBooking;

const styles = StyleSheet.create({
  itemTextStyle: {
    color: COLORS.GREEN,
    fontWeight: '500',
    fontFamily: FONTS.AvenirMedium,
    fontSize: 12,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  timeTextStyle: {
    color: COLORS.DARKER_GREY,
    fontWeight: '500',
    fontFamily: FONTS.AvenirMedium,
    fontSize: 12,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  itemWrapper: {
    height: 50,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
